'use strict'; // Proposed authenticated API contract. No prices, probabilities or rewards are derived here.

const fail = code => {
  throw new Error(code);
};

const object = v => !!v && typeof v === 'object' && !Array.isArray(v);

const id = v => typeof v === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(v);

const hash = v => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);

const integer = v => Number.isSafeInteger(v) && v >= 0;

const units = v => typeof v === 'string' && /^(0|[1-9][0-9]{0,35})$/.test(v);

const text = (v, n = 500) => typeof v === 'string' && v.length <= n && !/[\u0000-\u0008]/.test(v);

function canonical(v) {
  return JSON.stringify(v, (_, x) => object(x) ? Object.keys(x).sort().reduce((sorted, k) => {
    sorted[k] = x[k];
    return sorted;
  }, {}) : x);
}

function frozen(v) {
  if (v && typeof v === 'object') {
    Object.values(v).forEach(frozen);
    Object.freeze(v);
  }

  return v;
}

function copy(v) {
  return JSON.parse(JSON.stringify(v));
}

function check(ok, code) {
  if (!ok) fail(code);
}

function outcome(o) {
  check(object(o) && /^OP[0-9]{3}$/.test(o.cardId) && id(o.styleId) && object(o.rewards) && id(o.policyVersion) && hash(o.catalogueHash), 'INVALID_CARD_OUTCOME');
  check(Object.keys(o.rewards).length > 0 && Object.keys(o.rewards).length <= 20 && Object.entries(o.rewards).every(([k, v]) => id(k) && (integer(v) || units(v))), 'INVALID_REWARD_DESCRIPTION');
  return o;
}

function pack(v, expectedId) {
  check(object(v) && id(v.packId) && (!expectedId || v.packId === expectedId) && ['SEALED', 'REVEALED', 'OPENED'].includes(v.status), 'INVALID_PACK');
  if (v.status === 'SEALED') check(v.deliveryStatus === 'NOT_STARTED' && !('outcome' in v) && !('grant' in v) && !('card' in v) && !('style' in v), 'SEALED_ALLOCATION_LEAK');else outcome(v.outcome);
  if (v.status === 'REVEALED') check(v.deliveryStatus === 'NOT_STARTED' && !('grant' in v), 'PREMATURE_GRANT');

  if (v.status === 'OPENED') {
    check(['PENDING', 'PARTIAL', 'GRANTED'].includes(v.deliveryStatus) && object(v.grant) && id(v.grant.grantId) && Array.isArray(v.grant.components) && v.grant.components.length > 0, 'INVALID_GRANT_RECEIPT');
    const keys = new Set(),
          types = new Set();

    for (const c of v.grant.components) {
      check(id(c.componentId) && !keys.has(c.componentId) && id(c.type) && units(c.quantity) && c.quantity !== '0' && !types.has(c.type) && String(v.outcome.rewards[c.type]) === c.quantity && ['PENDING', 'GRANTED'].includes(c.status), 'INVALID_GRANT_COMPONENT');
      keys.add(c.componentId);
      types.add(c.type);
    }

    check(types.size === Object.keys(v.outcome.rewards).length, 'GRANT_REWARD_MISMATCH');
    check(v.deliveryStatus !== 'GRANTED' || v.grant.components.every(c => c.status === 'GRANTED'), 'FALSE_GRANTED_STATUS');
  }

  return frozen(copy(v));
}

function quote(v, skuId) {
  check(object(v) && id(v.quoteId) && hash(v.quoteHash) && id(v.skuId) && v.skuId === skuId && ['PAYABLE', 'CONFIRMED', 'CANCELLED'].includes(v.status) && integer(v.paidPacks) && v.paidPacks > 0 && integer(v.giftPacks) && ['CNY', 'HKD'].includes(v.currency) && units(v.amountMinor) && v.amountMinor !== '0' && integer(v.expiresAt) && v.immediatePaidGold === 0 && v.immediateFreeGold === 0 && id(v.policyVersion) && hash(v.catalogueHash), 'INVALID_CARD_QUOTE');
  check(object(v.paymentContext) && Object.keys(v.paymentContext).sort().join(',') === 'opCardQuoteHash,opCardQuoteId' && v.paymentContext.opCardQuoteId === v.quoteId && v.paymentContext.opCardQuoteHash === v.quoteHash, 'PAYMENT_CONTEXT_MISMATCH');
  return frozen(copy(v));
}

function catalogue(v) {
  check(object(v) && v.schema === 1 && hash(v.catalogueHash) && typeof v.purchasingEnabled === 'boolean' && Array.isArray(v.cards) && Array.isArray(v.packages), 'INVALID_CARD_CATALOGUE');
  const cardIds = new Set(),
        styleIds = new Set(),
        skus = new Set();

  for (const c of v.cards) {
    check(/^OP[0-9]{3}$/.test(c.cardId) && !cardIds.has(c.cardId) && text(c.name, 120) && text(c.rewardText, 1000) && Array.isArray(c.styles) && c.styles.length > 0, 'INVALID_CARD_DESCRIPTION');
    cardIds.add(c.cardId);

    for (const s of c.styles) {
      check(id(s) && !styleIds.has(s), 'DUPLICATE_CARD_STYLE');
      styleIds.add(s);
    }
  }

  for (const p of v.packages) {
    check(id(p.skuId) && !skus.has(p.skuId) && text(p.name, 120) && ['permanent', 'limited'].includes(p.kind) && integer(p.paidPacks) && p.paidPacks > 0 && integer(p.giftPacks) && p.immediatePaidGold === 0 && p.immediateFreeGold === 0, 'INVALID_PACK_DESCRIPTION');
    skus.add(p.skuId);
  }

  return frozen(copy(v));
}

function collection(v) {
  check(object(v) && Array.isArray(v.cards) && v.cards.length <= 60, 'INVALID_COLLECTION');
  const seen = new Set();

  for (const c of v.cards) {
    check(/^OP[0-9]{3}$/.test(c.cardId) && !seen.has(c.cardId) && units(c.count) && object(c.styles) && Object.entries(c.styles).every(([k, n]) => id(k) && units(n)), 'INVALID_COLLECTION_CARD');
    seen.add(c.cardId);
  }

  return frozen(copy(v));
}

function list(v) {
  check(object(v) && Array.isArray(v.items) && v.items.length <= 50 && (v.cursor === null || typeof v.cursor === 'string' && v.cursor.length <= 1024), 'INVALID_PACK_LIST');
  const seen = new Set();

  for (const p of v.items) {
    check(id(p.packId) && !seen.has(p.packId) && ['SEALED', 'REVEALED', 'OPENED'].includes(p.status) && ['NOT_STARTED', 'PENDING', 'PARTIAL', 'GRANTED'].includes(p.deliveryStatus), 'INVALID_PACK_LIST_ITEM');
    seen.add(p.packId);
    if (p.status === 'SEALED') check(!('outcome' in p) && !('card' in p) && !('style' in p) && !('grant' in p), 'SEALED_ALLOCATION_LEAK');
  }

  return frozen(copy(v));
}

function money(minor, currency) {
  check(units(minor) && ['CNY', 'HKD'].includes(currency), 'INVALID_PRICE_DISPLAY');
  return currency + ' ' + minor.padStart(3, '0').slice(0, -2) + '.' + minor.padStart(3, '0').slice(-2);
}

function createAPI({
  request,
  writesEnabled = false
} = {}) {
  check(typeof request === 'function', 'AUTHENTICATED_TRANSPORT_REQUIRED');

  async function call(method, path, body, validate) {
    if (method !== 'GET') check(writesEnabled === true, 'CARD_WRITES_DISABLED');
    const value = await request({
      method,
      path,
      body
    });
    return validate(value);
  }

  const path = p => {
    check(id(p), 'INVALID_PACK_ID');
    return '/op-cards/packs/' + p;
  };

  return Object.freeze({
    catalogue: hashValue => {
      check(hashValue === undefined || hash(hashValue), 'INVALID_CATALOGUE_HASH');
      return call('GET', '/op-cards/catalogue' + (hashValue ? '?catalogueHash=' + hashValue : ''), undefined, v => {
        const c = catalogue(v);
        check(!hashValue || c.catalogueHash === hashValue, 'HISTORICAL_CATALOGUE_MISMATCH');
        return c;
      });
    },
    collection: () => call('GET', '/op-cards/collection', undefined, collection),
    getQuote: quoteId => {
      check(id(quoteId), 'INVALID_QUOTE_ID');
      return call('GET', '/op-cards/quotes/' + quoteId, undefined, v => {
        check((v === null || v === void 0 ? void 0 : v.quoteId) === quoteId, 'QUOTE_ID_MISMATCH');
        return quote(v, v.skuId);
      });
    },
    packs: cursor => {
      check(cursor === undefined || cursor === null || typeof cursor === 'string' && cursor.length <= 1024, 'INVALID_PACK_CURSOR');
      return call('GET', '/op-cards/packs' + (cursor ? '?cursor=' + encodeURIComponent(cursor) : ''), undefined, list);
    },
    activity: activityId => {
      check(id(activityId), 'INVALID_ACTIVITY_ID');
      return call('GET', '/op-cards/activities/' + activityId, undefined, v => {
        check(object(v) && v.activityId === activityId && typeof v.enabled === 'boolean' && integer(v.nextStep) && v.nextStep >= 1 && v.nextStep <= 7 && typeof v.completed === 'boolean' && typeof v.purchasingEnabled === 'boolean', 'INVALID_ACTIVITY_PROGRESS');
        return frozen(copy(v));
      });
    },
    pack: p => call('GET', path(p), undefined, v => pack(v, p)),
    quote: input => {
      check(object(input) && id(input.skuId) && id(input.idempotencyKey) && (!input.activityId || id(input.activityId)) && Object.keys(input).every(k => ['skuId', 'activityId', 'idempotencyKey'].includes(k)), 'INVALID_QUOTE_INPUT');
      return call('POST', '/op-cards/quotes', copy(input), v => quote(v, input.skuId));
    },
    reveal: (p, requestId) => {
      check(id(requestId), 'REQUEST_ID_REQUIRED');
      return call('POST', path(p) + '/reveal', {
        requestId
      }, v => pack(v, p));
    },
    open: (p, requestId) => {
      check(id(requestId), 'REQUEST_ID_REQUIRED');
      return call('POST', path(p) + '/open', {
        requestId
      }, v => pack(v, p));
    }
  });
}

module.exports = {
  id,
  hash,
  check,
  canonical,
  frozen,
  pack,
  quote,
  catalogue,
  collection,
  list,
  money,
  createAPI
};
